import { useEffect, useSyncExternalStore } from 'react';

// One player at a time, across the whole app.
//
// The save turn puts the same post in two places at once: the card in the
// thread and the card in the panel beside it. A thread that collects several
// shared links stacks more. Without this store a customer who presses two
// discs hears two audio tracks together.
//
// It notifies rather than being a plain module flag, because the card that
// LOSES the slot is already on screen and would otherwise keep its player
// mounted and playing.
let activeId: string | null = null;
const listeners = new Set<() => void>();
let version = 0;

const emit = (): void => {
	version += 1;
	for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const getVersion = (): number => version;

export const claimSocialPlayer = (cardId: string): void => {
	if (activeId === cardId) return;
	activeId = cardId;
	emit();
};

export const releaseSocialPlayer = (cardId: string): void => {
	if (activeId !== cardId) return;
	activeId = null;
	emit();
};

/**
 * Whether this card holds the one player slot.
 *
 * The key is a per-mount id, never the post URL and never the artifact id. Two
 * mounts can show the SAME post, and a shared key would let both hold the slot.
 */
export function useIsActivePlayer(cardId: string): boolean {
	useSyncExternalStore(subscribe, getVersion, getVersion);
	useEffect(() => () => releaseSocialPlayer(cardId), [cardId]);
	return activeId === cardId;
}
